/**
 * Pixel propriétaire : tracking d'engagement du MultilingualAudioPlayer.
 * Envoi asynchrone et invisible vers le backend GraphQL.
 */

/** Clé partagée : même sessionId pour trackAudioAnalyticsEvent et createOrder (attribution audio → achat) */
const SESSION_STORAGE_KEY = "djeliba_session_id";
const LEGACY_SESSION_KEY = "audio_analytics_session_id";

/** Types d'événements alignés avec le backend (AudioAnalyticsEventType) - valeurs GraphQL enum */
export type AudioEventType =
  | "AUDIO_START"
  | "AUDIO_PROGRESS"
  | "AUDIO_LANGUAGE_CHANGE"
  | "AUDIO_COMPLETION";

/** Valeurs de l'enum AudioLanguage côté backend (GraphQL) */
export type AudioLanguageEnum = "WO" | "PL" | "FR";

const AUDIO_LANGUAGE_MAP: Record<string, AudioLanguageEnum> = {
  wo: "WO",
  pl: "PL",
  fr: "FR",
};

/** Convertit le code langue du lecteur (wo, pl, fr) en valeur enum GraphQL */
export function toAudioLanguageEnum(lang: string): AudioLanguageEnum | undefined {
  const normalized = lang?.toLowerCase();
  return normalized ? AUDIO_LANGUAGE_MAP[normalized] : undefined;
}

export interface AudioEventMetadata {
  productId: string;
  eventType: AudioEventType;
  language?: AudioLanguageEnum;
  progressPercent?: number;
  durationPlayed?: number;
}

/**
 * Retourne ou crée le sessionId partagé (audio analytics + createOrder).
 * À utiliser partout où l'attribution audio → achat est requise.
 */
export function getSessionId(): string {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = sessionStorage.getItem(LEGACY_SESSION_KEY);
      if (sessionId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
        sessionStorage.removeItem(LEGACY_SESSION_KEY);
      } else {
        sessionId =
          `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      }
    }
    return sessionId;
  } catch {
    return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }
}

function getGraphQLUri(): string {
  return (
    process.env.REACT_APP_GRAPHQL_URI || "https://lol.djeliba.com/graphql"
  );
}

const MUTATION = `
  mutation TrackAudioAnalyticsEvent($input: CreateAudioAnalyticsEventInput!) {
    trackAudioAnalyticsEvent(input: $input)
  }
`;

/**
 * Envoie un événement de tracking de manière asynchrone et non bloquante.
 * Utilise sendBeacon si disponible (garantit l'envoi même à la fermeture de page),
 * sinon fetch avec AbortController.
 */
export function trackAudioEvent(
  eventType: AudioEventType,
  metadata: Omit<AudioEventMetadata, "eventType">
): void {
  const sessionId = getSessionId();
  const input: Record<string, unknown> = {
    sessionId,
    productId: metadata.productId,
    eventType,
  };
  if (metadata.language != null) input.language = metadata.language;
  if (metadata.progressPercent != null)
    input.progressPercent = metadata.progressPercent;
  if (metadata.durationPlayed != null)
    input.durationPlayed = metadata.durationPlayed;

  const body = JSON.stringify({
    operationName: "TrackAudioAnalyticsEvent",
    query: MUTATION,
    variables: { input },
  });

  const url = getGraphQLUri();

  if (typeof navigator !== "undefined" && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    const sent = navigator.sendBeacon(url, blob);
    if (sent) return;
  }

  const controller = new AbortController();
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    signal: controller.signal,
    keepalive: true,
  }).catch(() => {
    /* silencieux : ne pas perturber l'UX */
  });
}
